import axios from '@/plugins/axios';
import utils from '@/stores/utils';

const URL = '/api/settings/contacts/';
const DEFAULT_FILTERS = { offset: 0, limit: 50, ordering: 'last_name' };

const state = {
    contacts: [],
    contacts_filters: { ...DEFAULT_FILTERS },
    contacts_count: 0,
    contacts_loaded: false,
    contacts_loading: false,
    contacts_loading_errors: null,
    contact_saving: false,
    contact_saving_errors: {},
    cancelTokenSource: null,
};

const mutations = {
    updateContacts(state, contacts) {
        state.contacts = contacts;
        state.contacts_loaded = true;
        state.contacts_loading = false;
        state.contacts_loading_errors = null;
    },
    updateContactsCount(state, contacts_count) {
        state.contacts_count = contacts_count;
    },
    updateContactsLoading(state, contacts_loading) {
        state.contacts_loading = contacts_loading;
    },
    updateContactsLoadingErrors(state, contacts_loading_errors) {
        state.contacts_loading_errors = contacts_loading_errors;
        state.contacts_loading = false;
    },
    updateContactsFilters(state, filters) {
        state.contacts_filters = filters;
    },
    updateContactSaving(state, contact_saving) {
        state.contact_saving = contact_saving;
    },
    updateContactSavingErrors(state, contact_saving_errors) {
        state.contact_saving_errors = contact_saving_errors || {};
    },
    updateCancelTokenSource(state, source) {
        state.cancelTokenSource = source;
    },
};

const actions = {
    async fetchContacts({ commit, state }) {
        if (state.cancelTokenSource) {
            state.cancelTokenSource.cancel('New fetch started');
        }
        const source = axios.CancelToken.source();
        commit('updateCancelTokenSource', source);

        commit('updateContactsLoading', true);
        commit('updateContactsLoadingErrors', null);

        try {
            const response = await axios.get(URL, {
                params: state.contacts_filters,
                cancelToken: source.token,
            });
            commit('updateContacts', response.data.results);
            commit('updateContactsCount', response.data.count);
            return response.data;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateContactsLoadingErrors', error.details);
            throw error;
        } finally {
            commit('updateContactsLoading', false);
        }
    },

    async saveContact({ commit, dispatch }, params) {
        commit('updateContactSaving', true);
        commit('updateContactSavingErrors', {});
        try {
            const contact = params.contact;
            const response = contact.id
                ? await axios.patch(`${URL}${contact.id}/`, contact)
                : await axios.post(URL, contact);
            await dispatch('fetchContacts');
            return response.data;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updateContactSavingErrors', error.details);
            throw error;
        } finally {
            commit('updateContactSaving', false);
        }
    },

    async deleteContact({ dispatch }, params) {
        try {
            const response = await axios.delete(`${URL}${params.contact.id}/`);
            await dispatch('fetchContacts');
            return response;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async setPrimaryContact({ dispatch }, params) {
        try {
            const response = await axios.post(`${URL}${params.contact.id}/set-primary/`);
            await dispatch('fetchContacts');
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
};
